import Fuse, { IFuseOptions } from 'fuse.js';
import { Injectable, inject } from '@angular/core';
import {
  SearchSnapshotService, SearchSnapshotStreamSnapshot,
} from './search-snapshot.service';
import {
  ArtistList, BrandList, isNullOrUndefinedOrEmpty, KeywordList, OrganizerList,
  SearchSnapshot,
} from 'in-time-core';

type SearchEntry = {
  name: string,
  uniqueId: string,
}

@Injectable({ providedIn: 'root' })
export class PublicProfileSearchService {
  static readonly DEFAULT_FUSE_OPTIONS = {
    includeScore: true,
    shouldSort: true,
    ignoreLocation: true,
    threshold: 0.4,
    keys: ['name'],
  } satisfies IFuseOptions<SearchEntry>;

  static readonly DEFAULT_SCORE_THRESHOLD = 0.4;

  private readonly searchSnapshotService: SearchSnapshotService = inject(SearchSnapshotService);

  private isInitialized = false;

  private fuse: Fuse<SearchEntry> | null = null;

  async ensureInitialized(): Promise<void> {
    if(this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    await this.searchSnapshotService.ensureInitialized();

    this.searchSnapshotService.searchSnapshots$.subscribe(
      (snapshot) => this.onSearchSnapshot(snapshot),
    );
  }

  search(query: string): string[] | null {
    if(this.fuse == null) {
      return null;
    }

    if(isNullOrUndefinedOrEmpty(query.trim())) {
      return null;
    }

    const fuseResult = this.fuse.search(query);
    const scoreThreshold = PublicProfileSearchService.DEFAULT_SCORE_THRESHOLD;
    // Scores are between 0.0 and 1.0, where 0.0 is the best match and 1.0 is
    // the worst match.

    const result = [...new Set<string>(fuseResult
      .filter((result) => (result.score ?? 0.0) < scoreThreshold)
      .map((result) => result.item.uniqueId))];

    return result;
  }

  private onSearchSnapshot(snapshot: SearchSnapshotStreamSnapshot | null): void {
    if(snapshot == null) {
      return;
    }

    if(!snapshot.success) {
      return;
    }

    const searchSnapshot = snapshot.data;

    const artistList = this.findArtistList(searchSnapshot);
    if(artistList == null) {
      return;
    }

    const brandList = this.findBrandList(searchSnapshot);
    if(brandList == null) {
      return;
    }

    const organizerList = this.findOrganizerList(searchSnapshot);
    if(organizerList == null) {
      return;
    }

    const keywordLists = this.findKeywordLists(searchSnapshot);
    if(keywordLists.length <= 0) {
      return;
    }

    const entries = [...this.generateSearchEntries({ artistList, brandList, organizerList, keywordLists })];

    this.fuse = new Fuse(entries, PublicProfileSearchService.DEFAULT_FUSE_OPTIONS);
  }

  private *generateSearchEntries({ artistList, brandList, organizerList, keywordLists }: {
    artistList: ArtistList,
    brandList: BrandList,
    organizerList: OrganizerList,
    keywordLists: KeywordList[]
  }): Generator<SearchEntry> {
    const keywordListTable = new Map<number, KeywordList>();
    for(const keywordList of keywordLists) {
      keywordListTable.set(keywordList.listId.asNumber(), keywordList);
    }

    for(const artist of artistList.artists) {
      const uniqueId = artist.uniqueId.asString();

      for(const keywordRef of artist.keywords) {
        const listId = keywordRef.listId.asNumber();
        const keywordList = keywordListTable.get(listId);

        if(keywordList == null) {
          continue;
        }

        const keywordIndex = keywordRef.index.asNumber();
        const keyword = keywordList.keywords[keywordIndex]?.asString() ?? null;
        if(isNullOrUndefinedOrEmpty(keyword)) {
          continue;
        }

        yield {
          name: keyword,
          uniqueId: uniqueId
        };
      }
    }

    for(const brand of brandList.brands) {
      const uniqueId = brand.uniqueId.asString();

      for(const keywordRef of brand.keywords) {
        const listId = keywordRef.listId.asNumber();
        const keywordList = keywordListTable.get(listId);

        if(keywordList == null) {
          continue;
        }

        const keywordIndex = keywordRef.index.asNumber();
        const keyword = keywordList.keywords[keywordIndex]?.asString() ?? null;
        if(isNullOrUndefinedOrEmpty(keyword)) {
          continue;
        }

        yield {
          name: keyword,
          uniqueId: uniqueId
        };
      }
    }

    for(const organizer of organizerList.organizers) {
      const uniqueId = organizer.uniqueId.asString();

      for(const keywordRef of organizer.keywords) {
        const listId = keywordRef.listId.asNumber();
        const keywordList = keywordListTable.get(listId);

        if(keywordList == null) {
          continue;
        }

        const keywordIndex = keywordRef.index.asNumber();
        const keyword = keywordList.keywords[keywordIndex]?.asString() ?? null;
        if(isNullOrUndefinedOrEmpty(keyword)) {
          continue;
        }

        yield {
          name: keyword,
          uniqueId: uniqueId
        };
      }
    }
  }

  private findArtistList(searchSnapshot: SearchSnapshot): ArtistList | null {
    for(const element of searchSnapshot.elements) {
      if(element instanceof ArtistList) {
        return element;
      }
    }

    return null;
  }

  private findBrandList(searchSnapshot: SearchSnapshot): BrandList | null {
    for(const element of searchSnapshot.elements) {
      if(element instanceof BrandList) {
        return element;
      }
    }

    return null;
  }

  private findOrganizerList(searchSnapshot: SearchSnapshot): OrganizerList | null {
    for(const element of searchSnapshot.elements) {
      if(element instanceof OrganizerList) {
        return element;
      }
    }

    return null;
  }

  private findKeywordLists(searchSnapshot: SearchSnapshot): KeywordList[] {
    const keywordLists: KeywordList[] = [];

    for(const element of searchSnapshot.elements) {
      if(element instanceof KeywordList) {
        keywordLists.push(element);
      }
    }

    return keywordLists;
  }
}
